import { useCallback, useEffect, useRef, useState } from 'react'
import { scenarios } from '../data/scenarios'
import type { LexceTurn, Scenario, ScenarioId } from '../types/lexce'

export type LexceStatus = 'idle' | 'listening' | 'thinking' | 'working' | 'done'

const delayFor = (turn: LexceTurn, prev?: LexceTurn): number => {
  // A task card animates its own steps, so whatever follows it waits that out.
  if (prev && prev.kind === 'task') return 520 + prev.task.steps.length * 620
  switch (turn.kind) {
    case 'user':
      return 260
    case 'say':
      return 1150
    case 'task':
      return 480
    case 'card':
      return 460
    case 'chips':
      return 340
  }
}

const thinkingFor = (turn: LexceTurn | undefined): LexceStatus => {
  if (!turn) return 'done'
  if (turn.kind === 'say') return 'thinking'
  if (turn.kind === 'task' || turn.kind === 'card') return 'working'
  return 'done'
}

export function useLexceConversation(initial: ScenarioId = 'none') {
  const [activeId, setActiveId] = useState<ScenarioId>('none')
  const [turns, setTurns] = useState<LexceTurn[]>([])
  const [status, setStatus] = useState<LexceStatus>('idle')
  const queue = useRef<LexceTurn[]>([])
  const timer = useRef<number | null>(null)

  const clear = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = null
    queue.current = []
  }, [])

  const run = useCallback(
    (scenario: Scenario) => {
      clear()
      setActiveId(scenario.id)
      setTurns([])
      queue.current = [...scenario.turns]
      setStatus('listening')
    },
    [clear],
  )

  const start = useCallback(
    (id: Exclude<ScenarioId, 'none'>) => {
      const scenario = scenarios.find((s) => s.id === id)
      if (scenario) run(scenario)
    },
    [run],
  )

  const reset = useCallback(() => {
    clear()
    setTurns([])
    setActiveId('none')
    setStatus('idle')
  }, [clear])

  useEffect(() => {
    if (status === 'idle' || status === 'done') return
    const next = queue.current[0]
    if (!next) {
      setStatus('done')
      return
    }
    const wait = status === 'listening' ? 1500 : delayFor(next, turns[turns.length - 1])
    timer.current = window.setTimeout(() => {
      queue.current = queue.current.slice(1)
      setTurns((prev) => [...prev, next])
      setStatus(thinkingFor(queue.current[0]))
    }, wait)
    return () => {
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [status, turns.length])

  useEffect(() => {
    if (initial !== 'none') start(initial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => clear, [clear])

  return { activeId, turns, status, start, reset }
}
